
import { defineComponent, computed, ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import RegisterFlow from '@/components/registerTeam/RegisterFlow.vue'
import StoreUtil from '@/stores/StoreUtil'
import InputText from '@/components/common/field/InputText.vue'
import useMaintenanceStatus from '@/Hooks/useMaintenanceStatus'

const organizationTypeMaps = [
  {
    displayCode: 'registerTeam.sportTypeSelect_soccer',
    apiKey: 'FOOTBALL',
    disabled: false,
  },
  {
    displayCode: 'registerTeam.sportTypeSelect_basketball',
    apiKey: 'BASKETBALL',
    disabled: false,
  },
  {
    displayCode: 'registerTeam.sportTypeSelect_rugby',
    apiKey: 'RUGBY',
    disabled: false,
  },
  {
    displayCode: 'registerTeam.sportTypeSelect_futsal',
    apiKey: 'FUTSAL',
    disabled: false,
  },
  {
    displayCode: 'registerTeam.sportTypeSelect_sports',
    apiKey: 'SPORTS',
    disabled: false,
  },
]

export default defineComponent({
  name: 'RegisterTeamPage',
  setup() {
    const inFieldTeamName = ref<InstanceType<typeof InputText>>()
    const router = useRouter()
    const { fetchMaintenanceStatus, title, message, availableStatus } = useMaintenanceStatus()
    const { organizationType } = router.currentRoute.value.query
    const { planType } = router.currentRoute.value.query
    const registerTeamStore = StoreUtil.useStore('RegisterTeamStore')
    // URL パラメーターに有効な組織種別があったらそれを初期設定する。
    if (organizationType) {
      const organizationTypes = organizationTypeMaps.map(typeMap => typeMap.apiKey)
      registerTeamStore.organizationType = organizationTypes.includes(
        (organizationType as string).toUpperCase(),
      )
        ? (organizationType as string).toUpperCase()
        : organizationTypes[0]
    }
    if (planType) registerTeamStore.planType = planType as string

    onMounted(() => {
      fetchMaintenanceStatus()
    })

    /**
     * MBP向けビルドかどうか
     */
    const isMbpBuild = computed(() => {
      return process.env.VUE_APP_BUILD_TARGET === 'mbp'
    })

    /**
     * 利用可能なサーバ一覧
     */
    const apiBaseUrlMaps = Object.entries(JSON.parse(process.env.VUE_APP_ALL_API_BASE_URL || {}))
      .filter(
        ([key]) => !((isMbpBuild.value || registerTeamStore.isOfflinePlan.value) && key === 'Asia'),
      )
      .map(([key, value]) => ({ key, value, displayCode: `registerTeam.serveSelect.${key}` }))

    /**
     * 選択したサーバが利用可能かどうか
     * メンテナンス中の場合 false を返す。
     */
    const isServerAvailable = computed(() => {
      const server = apiBaseUrlMaps.find(url => url.value === registerTeamStore.apiBaseUrl)?.key as
        | 'Asia'
        | 'Europe'
        | 'America'
        | undefined
      return server ? availableStatus.value[server] : true
    })

    /**
     * 登録ボタンの押下可否
     */
    const isEnableButton = computed(
      () =>
        inFieldTeamName.value?.meta.valid &&
        registerTeamStore.organizationType &&
        registerTeamStore.apiBaseUrl &&
        (!isMbpBuild.value || registerTeamStore.courseId) &&
        isServerAvailable.value,
    )

    watch(
      () => registerTeamStore.apiBaseUrl,
      async () => {
        if (isMbpBuild.value) {
          registerTeamStore.courseId = ''
          registerTeamStore.courseList = []
          await registerTeamStore.fetchCourseList()
        }
      },
    )

    return {
      isEnableButton,
      organizationTypeMaps,
      apiBaseUrlMaps,
      registerTeamStore,
      inFieldTeamName,
      isMbpBuild,
      ...useI18n(),
      onClickSubmit() {
        router.push({ name: 'AuthenticateUserPage' })
      },
      isServerAvailable,
      maintenanceTitle: title,
      maintenanceMessage: message,
    }
  },
  components: {
    RegisterFlow,
    'input-text': InputText,
  },
})
